import { Result } from '../../common-models/Result';
import { HttpServiceBase } from '../../common-services/Http/HttpServiceBase';
import {
  OrderCorrectionCommand,
  OrderCorrectionDiagnosticItem,
  OrderCorrectionDiagnosticsBatch,
  OrderCorrectionExecuteCommand,
  OrderCorrectionForm,
  OrderCorrectionGatewayResponse,
  OrderCorrectionInvoiceRecoveryCommand,
  OrderCorrectionInvoiceRecoveryPreparation,
  OrderCorrectionOperation,
  OrderCorrectionPreview,
  OrderCorrectionReprocessCommand,
  OrderCorrectionReprocessPreparation
} from './OrderCorrectionModels';

type OrderCorrectionResult<TData> = Result<OrderCorrectionGatewayResponse<TData>>;

interface HttpErrorWithResult<TData> {
  response?: {
    data?: OrderCorrectionResult<TData>;
  };
}

export class OrderCorrectionService extends HttpServiceBase {
  private readonly basePath = `${this.baseUrlSislogV2Api}/v2/order-correction`;

  diagnose = (orderNumbers: number[]): Promise<OrderCorrectionResult<OrderCorrectionDiagnosticsBatch> | null> => {
    const normalizedOrderNumbers = Array.from(new Set(orderNumbers.map(this.ensureOrderNumber)));

    if (normalizedOrderNumbers.length === 0) {
      throw new Error('Informe ao menos um numero de pedido para o diagnostico.');
    }

    if (normalizedOrderNumbers.length > 100) {
      throw new Error('O diagnostico aceita no maximo 100 pedidos por chamada.');
    }

    return this.request(() => this.http.post<
      OrderCorrectionResult<OrderCorrectionDiagnosticsBatch>,
      { orderNumbers: number[] }
    >(`${this.basePath}/diagnostics`, { orderNumbers: normalizedOrderNumbers }));
  };

  getDiagnostic = (orderNumber: number): Promise<OrderCorrectionResult<OrderCorrectionDiagnosticItem> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.get<OrderCorrectionResult<OrderCorrectionDiagnosticItem>>(
      `${this.basePath}/${canonicalOrderNumber}/diagnostic`
    ));
  };

  getCorrectionForm = (orderNumber: number): Promise<OrderCorrectionResult<OrderCorrectionForm> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.get<OrderCorrectionResult<OrderCorrectionForm>>(
      `${this.basePath}/${canonicalOrderNumber}/correction-form`
    ));
  };

  previewShopify = (
    orderNumber: number,
    command: OrderCorrectionCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionPreview> | null> =>
    this.postCorrection(orderNumber, 'shopify/preview', command);

  executeShopify = (
    orderNumber: number,
    command: OrderCorrectionExecuteCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> =>
    this.postCorrection(orderNumber, 'shopify/execute', command);

  recoverShopify = (
    orderNumber: number,
    command: OrderCorrectionExecuteCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> =>
    this.postCorrection(orderNumber, 'shopify/recover', command);

  previewProtheus = (
    orderNumber: number,
    command: OrderCorrectionCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionPreview> | null> =>
    this.postCorrection(orderNumber, 'protheus-customer/preview', command);

  executeProtheus = (
    orderNumber: number,
    command: OrderCorrectionExecuteCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> =>
    this.postCorrection(orderNumber, 'protheus-customer/execute', command);

  recoverProtheus = (
    orderNumber: number,
    command: OrderCorrectionExecuteCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> =>
    this.postCorrection(orderNumber, 'protheus-customer/recover', command);

  prepareReprocess = (
    orderNumber: number,
    command: OrderCorrectionReprocessCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionReprocessPreparation> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.post<
      OrderCorrectionResult<OrderCorrectionReprocessPreparation>,
      OrderCorrectionReprocessCommand
    >(`${this.basePath}/${canonicalOrderNumber}/reprocess/prepare`, command));
  };

  reprocess = (
    orderNumber: number,
    command: OrderCorrectionReprocessCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.post<
      OrderCorrectionResult<OrderCorrectionOperation>,
      OrderCorrectionReprocessCommand
    >(`${this.basePath}/${canonicalOrderNumber}/reprocess`, command));
  };

  recoverReprocess = (
    orderNumber: number,
    command: OrderCorrectionReprocessCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.post<
      OrderCorrectionResult<OrderCorrectionOperation>,
      OrderCorrectionReprocessCommand
    >(`${this.basePath}/${canonicalOrderNumber}/reprocess/recover`, command));
  };

  prepareInvoiceRecovery = (
    orderNumber: number,
    command: OrderCorrectionInvoiceRecoveryCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionInvoiceRecoveryPreparation> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.post<
      OrderCorrectionResult<OrderCorrectionInvoiceRecoveryPreparation>,
      OrderCorrectionInvoiceRecoveryCommand
    >(`${this.basePath}/${canonicalOrderNumber}/invoice-recovery/prepare`, command));
  };

  executeInvoiceRecovery = (
    orderNumber: number,
    command: OrderCorrectionInvoiceRecoveryCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.post<
      OrderCorrectionResult<OrderCorrectionOperation>,
      OrderCorrectionInvoiceRecoveryCommand
    >(`${this.basePath}/${canonicalOrderNumber}/invoice-recovery`, command));
  };

  recoverInvoiceRecovery = (
    orderNumber: number,
    command: OrderCorrectionInvoiceRecoveryCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.post<
      OrderCorrectionResult<OrderCorrectionOperation>,
      OrderCorrectionInvoiceRecoveryCommand
    >(`${this.basePath}/${canonicalOrderNumber}/invoice-recovery/recover`, command));
  };

  getOperation = (
    orderNumber: number,
    operationId: string
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    const canonicalOperationId = this.ensureOperationId(operationId);
    return this.request(() => this.http.get<OrderCorrectionResult<OrderCorrectionOperation>>(
      `${this.basePath}/${canonicalOrderNumber}/operations/${canonicalOperationId}`
    ));
  };

  reconcile = (
    orderNumber: number,
    operationId: string,
    command: OrderCorrectionCommand
  ): Promise<OrderCorrectionResult<OrderCorrectionOperation> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    const canonicalOperationId = this.ensureOperationId(operationId);
    return this.request(() => this.http.post<
      OrderCorrectionResult<OrderCorrectionOperation>,
      OrderCorrectionCommand
    >(`${this.basePath}/${canonicalOrderNumber}/operations/${canonicalOperationId}/reconcile`, command));
  };

  private postCorrection = <TCommand, TResult>(
    orderNumber: number,
    path: string,
    command: TCommand
  ): Promise<OrderCorrectionResult<TResult> | null> => {
    const canonicalOrderNumber = this.ensureOrderNumber(orderNumber);
    return this.request(() => this.http.post<OrderCorrectionResult<TResult>, TCommand>(
      `${this.basePath}/${canonicalOrderNumber}/corrections/${path}`,
      command
    ));
  };

  private request = async <TData>(
    execute: () => Promise<OrderCorrectionResult<TData> | null>
  ): Promise<OrderCorrectionResult<TData> | null> => {
    try {
      return await execute();
    } catch (error) {
      const result = (error as HttpErrorWithResult<TData>)?.response?.data;

      // O BFF devolve o mesmo Result<T> nos status nao-2xx. Retornamos o
      // envelope para a tela conseguir exibir erro e eventual operacao incerta.
      if (result && typeof result === 'object' && 'value' in result) {
        return result;
      }

      throw error;
    }
  };

  private ensureOrderNumber = (orderNumber: number): number => {
    if (!Number.isSafeInteger(orderNumber) || orderNumber <= 0) {
      throw new Error(`Numero de pedido invalido: ${String(orderNumber)}`);
    }

    return orderNumber;
  };

  private ensureOperationId = (operationId: string): string => {
    const normalized = operationId.trim();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalized)) {
      throw new Error('Identificador de operacao invalido.');
    }

    return normalized;
  };
}
